import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  CalendarClock,
  Plus,
  X,
  MapPin,
  Phone,
  Video,
  Pencil,
  Ban,
  Download,
  Users as UsersIcon,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  LayoutList,
  CalendarDays,
  Calendar as CalendarIcon,
} from "lucide-react";

const DEFAULT_DURATION_MIN = 60;
const DEFAULT_TZ = "America/New_York";

function pad(n) { return String(n).padStart(2, "0"); }

// Build "datetime-local" value (YYYY-MM-DDTHH:MM) from a Date, in the BROWSER's local TZ.
function toLocalInputValue(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultStartTime() {
  const d = new Date();
  // Next hour, top of hour
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}

function formatWhen(iso, tz) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz || undefined,
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

function formatRange(startIso, endIso, tz) {
  try {
    const s = new Date(startIso);
    const e = new Date(endIso);
    const datePart = s.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: tz || undefined });
    const t1 = s.toLocaleString(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz || undefined });
    const t2 = e.toLocaleString(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz || undefined, timeZoneName: "short" });
    return `${datePart} · ${t1}–${t2}`;
  } catch {
    return `${startIso} – ${endIso}`;
  }
}

function StatusBadge({ status }) {
  const cfg = {
    scheduled: { label: "Scheduled", cls: "bg-[#E8ECE7] text-[#166534]" },
    completed: { label: "Completed", cls: "bg-[#E0E7FF] text-[#1E3A8A]" },
    cancelled: { label: "Cancelled", cls: "bg-[#FEE2E2] text-[#991B1B]" },
    no_show: { label: "No-show", cls: "bg-[#FEF3C7] text-[#92400E]" },
  }[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

export default function Appointments() {
  const { user, can } = useAuth();
  const [params] = useSearchParams();
  const [appointments, setAppointments] = useState([]);
  const [careSeekers, setCareSeekers] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(params.get("new") === "1");
  const [editTarget, setEditTarget] = useState(null);
  const [filterMine, setFilterMine] = useState(false);
  const [filterStatus, setFilterStatus] = useState("upcoming");
  const [noteFromAppt, setNoteFromAppt] = useState(null);
  const [view, setView] = useState("list"); // 'list' | 'week' | 'day'
  const [anchorDate, setAnchorDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  // Pre-fill new-appointment dialog when user clicks an empty calendar slot
  const [newApptDefaults, setNewApptDefaults] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [apptRes, csRes] = await Promise.all([
        api.get("/appointments"),
        api.get("/care-seekers"),
      ]);
      setAppointments(apptRes.data || []);
      setCareSeekers(csRes.data || []);
      // /users is admin/ED-only — silently skip the toast for non-admin
      // staff (Care Navigators / Field Specialists). Names just fall
      // back to the assigned-to id in that case.
      try {
        const usersRes = await api.get("/users");
        setStaffUsers(usersRes.data || []);
      } catch (_e) { /* soft-fail */ }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seekerById = useMemo(() => Object.fromEntries(careSeekers.map((c) => [c.id, c])), [careSeekers]);
  const staffById = useMemo(() => Object.fromEntries(staffUsers.map((u) => [u.id, u])), [staffUsers]);

  const seekerName = (id) => {
    const c = seekerById[id];
    if (!c) return "—";
    return c.preferred_name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
  };

  const visible = useMemo(() => {
    const now = Date.now();
    return appointments.filter((a) => {
      if (filterMine && a.staff_user_id !== user?.id) return false;
      if (filterStatus === "upcoming") {
        if (a.status === "cancelled") return false;
        return new Date(a.end_at).getTime() >= now;
      }
      if (filterStatus === "past") return new Date(a.end_at).getTime() < now;
      if (filterStatus === "cancelled") return a.status === "cancelled";
      return true;
    });
  }, [appointments, filterMine, filterStatus, user]);

  const handleCancel = async (a) => {
    if (!window.confirm(`Cancel "${a.title}" with ${seekerName(a.care_seeker_id)}?\n\nA cancellation email will be sent to all attendees.`)) return;
    try {
      await api.post(`/appointments/${a.id}/cancel`);
      toast.success("Appointment cancelled. Attendees notified.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleComplete = async (a) => {
    try {
      await api.patch(`/appointments/${a.id}`, { status: "completed" });
      // Open a pre-filled wellness note dialog so navigators can capture outcomes
      // in seconds rather than retyping appointment details.
      setNoteFromAppt(a);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadIcs = async (a) => {
    try {
      const res = await api.get(`/appointments/${a.id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `harborwell-appointment-${a.id.slice(0, 8)}.ics`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canEdit = !!can?.("appointments.edit");
  const canCreate = !!can?.("appointments.create");

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Coordinate care</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Appointments</h1>
          <p className="text-sm text-[#1E3A8A] mt-1">Schedule meetings with Care Seekers. Outlook/Google calendar invites are emailed automatically.</p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="schedule-appointment-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] transition-colors"
          >
            <Plus className="w-4 h-4" /> Schedule
          </button>
        )}
      </div>

      {/* Filters + view toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex p-1 rounded-full bg-white border border-[#E5E5E0]">
          {["upcoming", "past", "cancelled", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${
                filterStatus === s ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-[#334155] ml-2">
          <input
            type="checkbox"
            checked={filterMine}
            onChange={(e) => setFilterMine(e.target.checked)}
            data-testid="filter-mine"
            className="rounded"
          />
          Only mine
        </label>
        <div className="ml-auto inline-flex p-1 rounded-full bg-white border border-[#E5E5E0]" data-testid="view-toggle">
          {[
            { key: "list", label: "List", icon: LayoutList },
            { key: "week", label: "Week", icon: CalendarDays },
            { key: "day", label: "Day", icon: CalendarIcon },
          ].map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              data-testid={`view-${v.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                view === v.key ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A]"
              }`}
            >
              <v.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : view !== "list" ? (
        <CalendarGrid
          mode={view}
          anchorDate={anchorDate}
          setAnchorDate={setAnchorDate}
          appointments={appointments}
          filterMine={filterMine}
          currentUserId={user?.id}
          seekerName={seekerName}
          onSelectAppointment={(a) => setEditTarget(a)}
          onSelectSlot={(date) => {
            // Open scheduling dialog with this start time pre-filled
            setNewApptDefaults({ start: date });
            setShowForm(true);
          }}
        />
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E5E5E0] p-10 text-center" data-testid="appointments-empty">
          <CalendarClock className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-[#334155] font-medium">No appointments yet</p>
          <p className="text-sm text-[#1E3A8A] mt-1">Click <strong>Schedule</strong> to invite a Care Seeker for a meeting.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="appointments-list">
          {visible.map((a) => {
            const staff = staffById[a.staff_user_id];
            const loc = [
              a.video_link && { icon: Video, label: a.video_link },
              a.phone && { icon: Phone, label: a.phone },
              a.physical_address && { icon: MapPin, label: a.physical_address },
            ].filter(Boolean);
            const isPast = new Date(a.end_at).getTime() < Date.now();
            const cancelled = a.status === "cancelled";
            return (
              <li
                key={a.id}
                data-testid={`appointment-${a.id}`}
                className={`bg-white border rounded-2xl p-4 sm:p-5 ${cancelled ? "border-[#FCA5A5] opacity-75" : "border-[#E5E5E0]"}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`font-semibold text-[#0F172A] ${cancelled ? "line-through" : ""}`}>{a.title}</h3>
                      <StatusBadge status={a.status} />
                    </div>
                    <p className="text-sm text-[#1E3A8A] font-medium mt-1">{formatRange(a.start_at, a.end_at, a.timezone)}</p>
                    <p className="text-xs text-[#1E3A8A] mt-0.5">
                      With <strong className="text-[#334155]">{seekerName(a.care_seeker_id)}</strong> · Hosted by {staff?.name || "—"}
                    </p>
                    {loc.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#334155]">
                        {loc.map(({ icon: Icon, label }, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1.5">
                            <Icon className="w-3.5 h-3.5 text-[#1E3A8A]" strokeWidth={1.75} />
                            <span className="truncate max-w-[280px]">{label}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {a.description && (
                      <p className="text-xs text-[#334155] mt-2 whitespace-pre-wrap line-clamp-3">{a.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <button
                      onClick={() => downloadIcs(a)}
                      title="Download .ics"
                      data-testid={`download-ics-${a.id}`}
                      className="p-2 rounded-full text-[#1E3A8A] hover:bg-[#F9F8F6] hover:text-[#1E3A8A]"
                    >
                      <Download className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                    {canEdit && !cancelled && !isPast && (
                      <button
                        onClick={() => setEditTarget(a)}
                        title="Reschedule / edit"
                        data-testid={`edit-${a.id}`}
                        className="p-2 rounded-full text-[#1E3A8A] hover:bg-[#F9F8F6] hover:text-[#1E3A8A]"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    )}
                    {canEdit && !cancelled && !isPast && (
                      <button
                        onClick={() => handleComplete(a)}
                        title="Mark completed"
                        data-testid={`complete-${a.id}`}
                        className="p-2 rounded-full text-[#1E3A8A] hover:bg-[#F9F8F6] hover:text-[#166534]"
                      >
                        <CheckCircle2 className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    )}
                    {canEdit && !cancelled && (
                      <button
                        onClick={() => handleCancel(a)}
                        title="Cancel"
                        data-testid={`cancel-${a.id}`}
                        className="p-2 rounded-full text-[#1E3A8A] hover:bg-[#FEE2E2] hover:text-[#991B1B]"
                      >
                        <Ban className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {(showForm || editTarget) && (
        <AppointmentDialog
          target={editTarget}
          defaultStart={newApptDefaults?.start || null}
          careSeekers={careSeekers}
          staffUsers={staffUsers}
          currentUserId={user?.id}
          onClose={() => { setShowForm(false); setEditTarget(null); setNewApptDefaults(null); }}
          onSaved={() => { setShowForm(false); setEditTarget(null); setNewApptDefaults(null); load(); }}
        />
      )}

      {noteFromAppt && (
        <ConvertToNoteDialog
          appointment={noteFromAppt}
          careSeekerName={seekerName(noteFromAppt.care_seeker_id)}
          onClose={() => { setNoteFromAppt(null); load(); }}
          onSaved={() => { setNoteFromAppt(null); load(); }}
        />
      )}
    </div>
  );
}

function AppointmentDialog({ target, defaultStart, careSeekers, staffUsers, currentUserId, onClose, onSaved }) {
  const editing = !!target;
  const startSeed = useMemo(
    () => editing ? new Date(target.start_at) : (defaultStart || defaultStartTime()),
    [editing, target, defaultStart],
  );
  const endSeed = useMemo(() => {
    if (editing) return new Date(target.end_at);
    const e = new Date(startSeed);
    e.setMinutes(e.getMinutes() + DEFAULT_DURATION_MIN);
    return e;
  }, [editing, target, startSeed]);

  const [form, setForm] = useState({
    care_seeker_id: target?.care_seeker_id || "",
    staff_user_id: target?.staff_user_id || currentUserId || "",
    title: target?.title || "",
    description: target?.description || "",
    start_local: toLocalInputValue(startSeed),
    end_local: toLocalInputValue(endSeed),
    physical_address: target?.physical_address || "",
    phone: target?.phone || "",
    video_link: target?.video_link || "",
    timezone: target?.timezone || DEFAULT_TZ,
  });
  const [saving, setSaving] = useState(false);

  const handleStartChange = (v) => {
    setForm((prev) => {
      const next = { ...prev, start_local: v };
      // Auto-bump end if it's before/equal to start
      if (v && prev.end_local && new Date(prev.end_local) <= new Date(v)) {
        const e = new Date(v);
        e.setMinutes(e.getMinutes() + DEFAULT_DURATION_MIN);
        next.end_local = toLocalInputValue(e);
      } else if (v && !prev.end_local) {
        const e = new Date(v);
        e.setMinutes(e.getMinutes() + DEFAULT_DURATION_MIN);
        next.end_local = toLocalInputValue(e);
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.care_seeker_id || !form.staff_user_id || !form.title || !form.start_local || !form.end_local) {
      toast.error("Please fill in Care Seeker, staff, title, and date/time.");
      return;
    }
    setSaving(true);
    try {
      const start_at = new Date(form.start_local).toISOString();
      const end_at = new Date(form.end_local).toISOString();
      const payload = {
        care_seeker_id: form.care_seeker_id,
        staff_user_id: form.staff_user_id,
        title: form.title.trim(),
        description: form.description.trim() || null,
        start_at,
        end_at,
        timezone: form.timezone || DEFAULT_TZ,
        physical_address: form.physical_address.trim() || null,
        phone: form.phone.trim() || null,
        video_link: form.video_link.trim() || null,
      };
      if (editing) {
        await api.patch(`/appointments/${target.id}`, payload);
        toast.success("Appointment updated. Attendees notified.");
      } else {
        await api.post("/appointments", payload);
        toast.success("Appointment scheduled. Invites sent.");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-[#0F172A]/40 overflow-y-auto" data-testid="appointment-dialog">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E5E0]">
          <div>
            <div className="hw-eyebrow text-[10px]">{editing ? "Reschedule" : "New appointment"}</div>
            <h2 className="font-display text-xl font-semibold text-[#0F172A] mt-1">
              {editing ? "Update appointment" : "Schedule an appointment"}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F9F8F6]" data-testid="dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4" data-testid="appointment-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Care Seeker</label>
              <select
                required
                value={form.care_seeker_id}
                onChange={(e) => setForm({ ...form, care_seeker_id: e.target.value })}
                data-testid="field-care-seeker"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                disabled={editing}
              >
                <option value="">Select…</option>
                {careSeekers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.preferred_name || c.first_name)} {c.last_name}{c.email ? "" : " (no email)"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Hosted by</label>
              <select
                required
                value={form.staff_user_id}
                onChange={(e) => setForm({ ...form, staff_user_id: e.target.value })}
                data-testid="field-staff"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              >
                <option value="">Select staff…</option>
                {staffUsers
                  .filter((u) => u.active && ["admin", "executive_director", "care_navigator", "field_specialist"].includes(u.role))
                  .map((u) => (
                    <option key={u.id} value={u.id}>{u.name} — {u.title || u.role}</option>
                  ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Title</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="field-title"
              placeholder="e.g., Wellness check-in, Housing intake review"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Starts</label>
              <input
                required
                type="datetime-local"
                value={form.start_local}
                onChange={(e) => handleStartChange(e.target.value)}
                data-testid="field-start"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Ends</label>
              <input
                required
                type="datetime-local"
                value={form.end_local}
                onChange={(e) => setForm({ ...form, end_local: e.target.value })}
                data-testid="field-end"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              />
              <p className="text-[10px] text-[#1E3A8A] mt-1">Default duration: 60 minutes — adjust as needed.</p>
            </div>
          </div>

          <div className="rounded-xl border border-[#E5E5E0] bg-[#F9F8F6] p-4 space-y-3">
            <p className="text-xs text-[#334155] font-medium uppercase tracking-wider">Location <span className="normal-case text-[#1E3A8A] font-normal">(any combination)</span></p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-[#1E3A8A] inline-flex items-center gap-1"><Video className="w-3 h-3" /> Microsoft Teams / Zoom link</label>
                <input
                  type="url"
                  value={form.video_link}
                  onChange={(e) => setForm({ ...form, video_link: e.target.value })}
                  data-testid="field-video"
                  placeholder="https://teams.microsoft.com/…"
                  className="mt-1 w-full px-2.5 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#1E3A8A]"
                />
              </div>
              <div>
                <label className="text-[10px] text-[#1E3A8A] inline-flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="field-phone"
                  placeholder="(252) 555-0100"
                  className="mt-1 w-full px-2.5 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#1E3A8A]"
                />
              </div>
              <div>
                <label className="text-[10px] text-[#1E3A8A] inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Physical address</label>
                <input
                  value={form.physical_address}
                  onChange={(e) => setForm({ ...form, physical_address: e.target.value })}
                  data-testid="field-address"
                  placeholder="Street, City, NC"
                  className="mt-1 w-full px-2.5 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#1E3A8A]"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Notes / agenda</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="field-description"
              placeholder="What you'll discuss, any documents the Care Seeker should bring, etc."
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
            />
          </div>

          <div className="rounded-xl bg-[#E8ECE7] p-3 text-xs text-[#166534] flex items-start gap-2">
            <UsersIcon className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
            <p>
              On save we'll email a branded HarborWell invite with a <strong>.ics calendar attachment</strong> to
              the staff member and the Care Seeker (if they have an email on file). Outlook / Google / Apple Calendar
              will all show an "Add to calendar" button.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#334155]">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="submit-appointment"
              className="px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60"
            >
              {saving ? "Saving…" : (editing ? "Update & resend invites" : "Schedule & send invites")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Week / Day calendar grid
// ============================================================
const GRID_START_HOUR = 7; // 7 AM
const GRID_END_HOUR = 20;  // 8 PM
const SLOT_MIN = 30;       // 30-min granularity
const SLOTS_PER_HOUR = 60 / SLOT_MIN;
const PX_PER_HOUR = 56;    // visual height of one hour row

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0=Sun
  x.setDate(x.getDate() - day);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function CalendarGrid({
  mode, // 'week' | 'day'
  anchorDate,
  setAnchorDate,
  appointments,
  filterMine,
  currentUserId,
  seekerName,
  onSelectAppointment,
  onSelectSlot,
}) {
  const days = useMemo(() => {
    if (mode === "day") return [anchorDate];
    const start = startOfWeek(anchorDate);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [anchorDate, mode]);

  const rangeStart = days[0];
  const rangeEnd = addDays(days[days.length - 1], 1);

  const visibleAppts = useMemo(() => {
    return appointments.filter((a) => {
      if (filterMine && a.staff_user_id !== currentUserId) return false;
      if (a.status === "cancelled") return false;
      const start = new Date(a.start_at);
      return start >= rangeStart && start < rangeEnd;
    });
  }, [appointments, filterMine, currentUserId, rangeStart, rangeEnd]);

  const byDay = useMemo(() => {
    const m = new Map();
    for (const a of visibleAppts) {
      const start = new Date(a.start_at);
      const idx = days.findIndex((d) => sameDay(d, start));
      if (idx < 0) continue;
      if (!m.has(idx)) m.set(idx, []);
      m.get(idx).push(a);
    }
    return m;
  }, [visibleAppts, days]);

  const hours = useMemo(() => {
    const arr = [];
    for (let h = GRID_START_HOUR; h <= GRID_END_HOUR; h++) arr.push(h);
    return arr;
  }, []);

  const totalRows = (GRID_END_HOUR - GRID_START_HOUR) * SLOTS_PER_HOUR;

  const computeBlock = (a) => {
    const s = new Date(a.start_at);
    const e = new Date(a.end_at);
    const startMin = s.getHours() * 60 + s.getMinutes();
    const endMin = e.getHours() * 60 + e.getMinutes();
    const gridStartMin = GRID_START_HOUR * 60;
    const gridEndMin = GRID_END_HOUR * 60;
    const clampedStart = Math.max(startMin, gridStartMin);
    const clampedEnd = Math.min(endMin, gridEndMin);
    if (clampedEnd <= clampedStart) return null;
    const top = ((clampedStart - gridStartMin) / 60) * PX_PER_HOUR;
    const height = Math.max(((clampedEnd - clampedStart) / 60) * PX_PER_HOUR, 18);
    return { top, height };
  };

  const headerLabel = () => {
    if (mode === "day") {
      return anchorDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    }
    const start = days[0];
    const end = days[days.length - 1];
    const sameMonth = start.getMonth() === end.getMonth();
    if (sameMonth) {
      return `${start.toLocaleDateString(undefined, { month: "long", day: "numeric" })} – ${end.toLocaleDateString(undefined, { day: "numeric", year: "numeric" })}`;
    }
    return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  };

  const goPrev = () => setAnchorDate((d) => addDays(d, mode === "day" ? -1 : -7));
  const goNext = () => setAnchorDate((d) => addDays(d, mode === "day" ? 1 : 7));
  const goToday = () => { const t = new Date(); t.setHours(0, 0, 0, 0); setAnchorDate(t); };

  const todayDate = new Date();

  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E0] overflow-hidden" data-testid={`calendar-${mode}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-[#E5E5E0]">
        <div className="flex items-center gap-1">
          <button onClick={goPrev} data-testid="cal-prev" className="p-2 rounded-full hover:bg-[#F9F8F6] text-[#334155]">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={goNext} data-testid="cal-next" className="p-2 rounded-full hover:bg-[#F9F8F6] text-[#334155]">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={goToday}
            data-testid="cal-today"
            className="ml-2 text-xs font-medium text-[#1E3A8A] px-3 py-1.5 rounded-full bg-[#E8ECE7] hover:bg-[#dfe6dc]"
          >
            Today
          </button>
        </div>
        <div className="font-display text-base font-semibold text-[#0F172A]" data-testid="cal-range-label">
          {headerLabel()}
        </div>
        <div className="text-xs text-[#1E3A8A]">
          {visibleAppts.length} appointment{visibleAppts.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid border-b border-[#E5E5E0]" style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(0, 1fr))` }}>
        <div />
        {days.map((d) => {
          const isToday = sameDay(d, todayDate);
          return (
            <button
              key={d.toISOString()}
              onClick={() => { setAnchorDate(d); }}
              data-testid={`cal-day-header-${d.getDay()}`}
              className={`px-2 py-2 text-center transition-colors ${isToday ? "bg-[#E0E7FF]" : "hover:bg-[#F9F8F6]"}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">
                {d.toLocaleDateString(undefined, { weekday: "short" })}
              </div>
              <div className={`text-lg font-display font-semibold ${isToday ? "text-[#1E3A8A]" : "text-[#0F172A]"}`}>
                {d.getDate()}
              </div>
            </button>
          );
        })}
      </div>

      <div className="overflow-auto" style={{ maxHeight: "70vh" }}>
        <div className="relative grid" style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(0, 1fr))` }}>
          <div className="border-r border-[#E5E5E0] bg-[#F9F8F6]/40">
            {hours.map((h) => (
              <div key={h} style={{ height: PX_PER_HOUR }} className="text-[10px] text-[#1E3A8A] text-right pr-2 pt-1 border-b border-[#F0EFEB]">
                {h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`}
              </div>
            ))}
          </div>

          {days.map((day, dayIdx) => {
            const isToday = sameDay(day, todayDate);
            const dayAppts = byDay.get(dayIdx) || [];
            return (
              <div
                key={day.toISOString()}
                data-testid={`cal-day-col-${dayIdx}`}
                className={`relative border-r border-[#E5E5E0] last:border-r-0 ${isToday ? "bg-[#FAFBFF]" : ""}`}
                style={{ height: totalRows * (PX_PER_HOUR / SLOTS_PER_HOUR) }}
              >
                {Array.from({ length: totalRows }).map((_, slotIdx) => {
                  const hour = GRID_START_HOUR + Math.floor(slotIdx / SLOTS_PER_HOUR);
                  const minute = (slotIdx % SLOTS_PER_HOUR) * SLOT_MIN;
                  const slotDate = new Date(day);
                  slotDate.setHours(hour, minute, 0, 0);
                  const isHourBoundary = minute === 0;
                  return (
                    <button
                      key={slotIdx}
                      onClick={() => onSelectSlot(slotDate)}
                      data-testid={`cal-slot-${dayIdx}-${hour}-${minute}`}
                      title={`Schedule at ${slotDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`}
                      className={`block w-full hover:bg-[#E8ECE7]/40 transition-colors ${isHourBoundary ? "border-b border-[#F0EFEB]" : "border-b border-dashed border-[#F0EFEB]"}`}
                      style={{ height: PX_PER_HOUR / SLOTS_PER_HOUR }}
                    />
                  );
                })}

                {dayAppts.map((a) => {
                  const geo = computeBlock(a);
                  if (!geo) return null;
                  return (
                    <button
                      key={a.id}
                      onClick={(e) => { e.stopPropagation(); onSelectAppointment(a); }}
                      data-testid={`cal-block-${a.id}`}
                      className="absolute left-1 right-1 rounded-lg bg-[#1E3A8A] text-white text-left px-2 py-1 shadow-md hover:bg-[#172554] transition-colors overflow-hidden"
                      style={{ top: geo.top, height: geo.height }}
                    >
                      <div className="text-[10px] opacity-90 leading-tight">
                        {new Date(a.start_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                      </div>
                      <div className="text-xs font-semibold leading-tight truncate">{a.title}</div>
                      {geo.height > 40 && (
                        <div className="text-[10px] opacity-80 truncate mt-0.5">{seekerName(a.care_seeker_id)}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <p className="px-4 py-2 text-[11px] text-[#1E3A8A] border-t border-[#E5E5E0] bg-[#F9F8F6]/40">
        Tip: Click an empty slot to schedule a new appointment, or click an existing block to view/edit.
      </p>
    </div>
  );
}




function ConvertToNoteDialog({ appointment, careSeekerName, onClose, onSaved }) {
  const a = appointment;
  // Pre-fill from the appointment
  const presetType = a.video_link ? "phone" : a.physical_address ? "in_person" : "phone";
  const locationLine = [
    a.video_link && `Video: ${a.video_link}`,
    a.phone && `Phone: ${a.phone}`,
    a.physical_address && `Address: ${a.physical_address}`,
  ].filter(Boolean).join(" · ");
  const prefilledNotes =
    `Appointment outcome: ${a.title}\n` +
    (locationLine ? `Location: ${locationLine}\n` : "") +
    (a.description ? `\nAgenda / preparation:\n${a.description}\n` : "") +
    `\n--- Outcomes & follow-up ---\n`;

  const [form, setForm] = useState({
    interaction_type: presetType,
    emotional_wellness_notes: "",
    resources_discussed: "",
    referrals_made: "",
    next_steps: "",
    follow_up_date: "",
    notes: prefilledNotes,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/notes", {
        care_seeker_id: a.care_seeker_id,
        interaction_date: a.start_at,
        interaction_type: form.interaction_type,
        emotional_wellness_notes: form.emotional_wellness_notes.trim() || null,
        resources_discussed: form.resources_discussed.trim() || null,
        referrals_made: form.referrals_made.trim() || null,
        next_steps: form.next_steps.trim() || null,
        follow_up_date: form.follow_up_date || null,
        notes: form.notes.trim() || null,
      });
      toast.success("Follow-up note created from appointment");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    toast.success("Appointment marked complete");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-[#0F172A]/40 overflow-y-auto" data-testid="convert-note-dialog">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E5E0]">
          <div>
            <div className="hw-eyebrow text-[10px]">Appointment completed</div>
            <h2 className="font-display text-xl font-semibold text-[#0F172A] mt-1">
              Convert to follow-up note
            </h2>
            <p className="text-xs text-[#1E3A8A] mt-1">
              With <strong className="text-[#334155]">{careSeekerName}</strong> · pre-filled from the appointment.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F9F8F6]" data-testid="convert-note-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3" data-testid="convert-note-form">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Interaction type</label>
            <select
              value={form.interaction_type}
              onChange={(e) => setForm({ ...form, interaction_type: e.target.value })}
              data-testid="convert-note-type"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
            >
              {["phone", "text", "email", "in_person", "home_visit", "outreach_event"].map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Outcome / notes</label>
            <textarea
              rows={6}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="convert-note-notes"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] font-mono"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Emotional wellness</label>
              <textarea
                rows={2}
                value={form.emotional_wellness_notes}
                onChange={(e) => setForm({ ...form, emotional_wellness_notes: e.target.value })}
                data-testid="convert-note-emotional"
                className="w-full px-3 py-2 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Resources discussed</label>
              <textarea
                rows={2}
                value={form.resources_discussed}
                onChange={(e) => setForm({ ...form, resources_discussed: e.target.value })}
                data-testid="convert-note-resources"
                className="w-full px-3 py-2 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Next steps</label>
              <textarea
                rows={2}
                value={form.next_steps}
                onChange={(e) => setForm({ ...form, next_steps: e.target.value })}
                data-testid="convert-note-next"
                className="w-full px-3 py-2 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Follow-up date</label>
              <input
                type="date"
                value={form.follow_up_date}
                onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                data-testid="convert-note-followup"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#F0EFEB] mt-4">
            <button
              type="button"
              onClick={skip}
              data-testid="convert-note-skip"
              className="text-sm text-[#1E3A8A] hover:text-[#334155]"
            >
              Skip — just mark complete
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="convert-note-save"
              className="px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save follow-up note"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
